/**
 * Terms and Conditions Page
 * 
 * Legal document outlining the terms of service for BRV Labs products and services.
 * This page is accessible via the footer and is designed for readability and compliance.
 */

import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";

export default function TermsAndConditions() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-cream-50 to-forest-50 py-16">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link to="/">
          <Button variant="ghost" className="mb-8">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Home
          </Button>
        </Link>

        {/* Page Header */}
        <div className="mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Terms and Conditions</h1>
          <p className="text-lg text-gray-600">Effective Date: December 4, 2025</p>
        </div>

        {/* Legal Content */}
        <div className="prose prose-lg max-w-none">
          <div className="bg-white rounded-lg shadow-sm p-8 space-y-8">
            <p className="text-gray-700 leading-relaxed">
              These Terms and Conditions ("Terms") govern your access to and use of products and services offered by BRV Labs ("BRV Labs," "we," "us," or "our"), including interactive books, micro‑lessons, animated videos, educational activity books, mediation services, and college advising (collectively, the "Services"). By accessing or using the Services, you agree to be bound by these Terms.
            </p>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">1. Eligibility and Supervision for Minors</h2>
              <p className="text-gray-700 leading-relaxed">
                BRV Labs creates content and programming designed for children's social‑emotional learning; however, accounts and purchases must be made by a parent, legal guardian, educator, or adult representative. Children may use our content only under adult supervision. By using the Services, you represent that you are at least 18 years old or have the consent and supervision of a parent or legal guardian.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">2. Accounts and Security</h2>
              <p className="text-gray-700 leading-relaxed">
                You may be required to create an account to access certain Services. You agree to provide accurate information, keep your login credentials confidential, and notify us promptly of any unauthorized use. You are responsible for all activity under your account.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">3. Purchases, Pricing, and Subscriptions</h2>
              <p className="text-gray-700 leading-relaxed mb-4">
                Prices are displayed at checkout and are subject to change at any time. Some Services may be offered as one‑time purchases (e.g., physical books, digital downloads) and others as subscriptions (e.g., ongoing micro‑lessons or content access). Taxes, shipping, and handling (if applicable) will be calculated at checkout.
              </p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Digital Content:</strong> Digital purchases are delivered electronically and are generally non‑refundable once accessed or downloaded.</li>
                <li><strong>Subscriptions:</strong> You can cancel future renewals at any time; cancellations take effect at the end of your current billing period.</li>
                <li><strong>Services (Mediation/Advising):</strong> Scheduled sessions may be rescheduled with at least 24 hours' notice; missed sessions may be charged in full.</li>
                <li><strong>Promotions/Discounts:</strong> Any promotional rates apply only as specifically stated and may be time‑limited.</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">4. Shipping and Delivery (Physical Products)</h2>
              <p className="text-gray-700 leading-relaxed">
                For physical books or materials, we will provide estimated shipping timelines and carriers at checkout. Title and risk of loss pass to you upon delivery. Please inspect items upon receipt and contact us for order‑related issues.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">5. Educational Content; No Professional Advice</h2>
              <p className="text-gray-700 leading-relaxed">
                Our content supports social‑emotional learning and skill‑building. It is <strong>not medical, psychological, therapeutic, or legal advice</strong> and does not replace individualized professional care. If you have concerns about a child's health, safety, or development, consult a qualified professional.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">6. Mediation Services</h2>
              <p className="text-gray-700 leading-relaxed">
                Mediation services aim to facilitate constructive dialogue and skillful conflict resolution. We do not guarantee outcomes or enforce agreements. Participation is voluntary, and parties remain responsible for their decisions and actions.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">7. College Advising</h2>
              <p className="text-gray-700 leading-relaxed">
                Advising provides guidance for students with executive functioning needs and related goals. BRV Labs does not guarantee admissions decisions, scholarships, or program placements.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">8. User Conduct</h2>
              <p className="text-gray-700 leading-relaxed mb-4">You agree not to:</p>
              <ul className="list-disc list-inside space-y-2 text-gray-700">
                <li><strong>Misuse the Services:</strong> Reverse‑engineer, interfere with security, or attempt unauthorized access.</li>
                <li><strong>Exploit Content:</strong> Copy, distribute, or modify materials beyond the license granted in Section 9.</li>
                <li><strong>Harm Others:</strong> Post or transmit content that is harmful, harassing, or violates any law.</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-4">
                We may suspend or terminate access for violations of these Terms.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">9. Intellectual Property and Limited License</h2>
              <p className="text-gray-700 leading-relaxed">
                All content—including text, graphics, animations, videos, lessons, logos, and trademarks—is owned by BRV Labs or licensed to us. Subject to your compliance with these Terms, we grant you a limited, non‑exclusive, non‑transferable license to access and use the content for personal, household, classroom, or professional development purposes. Any commercial use, reproduction, distribution, or derivative works require our prior written consent.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">10. Feedback</h2>
              <p className="text-gray-700 leading-relaxed">
                If you submit ideas or feedback, you grant us a perpetual, irrevocable, royalty‑free license to use them without restriction or obligation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">11. Privacy and Children's Information</h2>
              <p className="text-gray-700 leading-relaxed">
                We are committed to protecting children's privacy. Accounts are created and managed by adults. To the extent we collect limited information about child users (e.g., anonymized usage metrics within supervised sessions), we use it to improve our Services and safeguard learning experiences. For more details, see our{" "}
                <Link to="/privacy-policy" className="text-forest-600 hover:text-forest-700 underline">
                  Privacy Policy
                </Link>. Do not share sensitive personal information about minors through our Services beyond what is strictly necessary for participation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">12. Third‑Party Tools and Platforms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may integrate or reference third‑party services (e.g., payment, design, or content delivery tools). Your use of such services is subject to their separate terms. We are not responsible for third‑party platforms' practices or availability.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">13. Warranties and Disclaimers</h2>
              <p className="text-gray-700 leading-relaxed">
                The Services are provided <strong>"as is" and "as available."</strong> We disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, and non‑infringement. We do not warrant uninterrupted or error‑free operation.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">14. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                To the fullest extent permitted by law, BRV Labs and its affiliates will not be liable for indirect, incidental, special, consequential, or punitive damages, or for lost profits, revenues, or data, arising from or related to your use of the Services. Our total liability for any claim will not exceed the amounts you paid to BRV Labs for the applicable Service in the 12 months preceding the claim.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">15. Indemnification</h2>
              <p className="text-gray-700 leading-relaxed">
                You agree to indemnify and hold harmless BRV Labs from claims, losses, liabilities, and expenses (including reasonable attorneys' fees) arising out of your use of the Services, your violation of these Terms, or your infringement of third‑party rights.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">16. Termination</h2>
              <p className="text-gray-700 leading-relaxed">
                We may suspend or terminate access at any time for violation of these Terms or for operational reasons. Upon termination, the license granted in Section 9 ends and you must cease using our content, except for materials you acquired under a separate written license that expressly survives termination.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">17. Changes to the Services or Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update the Services and these Terms from time to time. If we make material changes, we will provide notice by posting the updated Terms or by other reasonable means. Changes become effective upon posting unless stated otherwise. Your continued use after changes indicates acceptance.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">18. Governing Law; Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms are governed by the laws of the State of [Your State], without regard to conflict‑of‑law principles. Any dispute will be resolved through good‑faith negotiation, followed by <strong>confidential mediation</strong>, and then binding arbitration in [Your County, Your State], except where prohibited by law. You and BRV Labs waive the right to a jury trial and class action to the maximum extent permitted.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">19. Severability; Entire Agreement; Assignment</h2>
              <p className="text-gray-700 leading-relaxed">
                If any provision is found unenforceable, the remaining provisions will remain in effect. These Terms constitute the entire agreement between you and BRV Labs regarding the Services and supersede prior agreements. You may not assign your rights without our consent; we may assign ours in connection with a merger, acquisition, or asset transfer.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-bold text-gray-900 mb-4">20. Contact</h2>
              <p className="text-gray-700 leading-relaxed">
                Questions about these Terms or the Services can be directed to:
              </p>
              <p className="text-gray-700 leading-relaxed mt-2">
                <strong>Email:</strong>{" "}
                <a href="mailto:admin@brvlabsbooks.co" className="text-forest-600 hover:text-forest-700 underline">
                  admin@brvlabsbooks.co
                </a>
              </p>
            </section>
          </div>
        </div>

        {/* Back to Top Button */}
        <div className="mt-12 text-center">
          <Link to="/">
            <Button className="bg-forest-600 hover:bg-forest-700 text-white">
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Home
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
